// Copyright © 2026 Alex Bruce. All rights reserved.
//
// The lessons library, backed by the org rather than the browser.
//
// best_practices has had an org_id column and RLS since migration 009, with a
// note saying the client would sync to it "in future". It never did, so every
// estimator accumulated a private library nobody else could see and a cleared
// cache destroyed. Edits land for the whole org immediately -- there is no
// draft state, by decision.
//
// Writes are a diff against what was on screen, not a wipe-and-rewrite: two
// people editing different lessons at the same time must not erase each
// other, and a failed write must not take the rest of the library with it.

import { supabase } from "./supabase";
import type { BestPractice, BestPracticeCategory } from "../types";

interface Row {
  id: string;
  org_id: string | null;
  user_id: string | null;
  description: string;
  division_tags: string[];
  category: string;
  typical_cost_note: string | null;
  source_note: string | null;
  created_at: string | null;
}

const CATEGORIES = ["eo", "scope_gap", "ve", "code", "owner_pref"] as const;
const asCategory = (c: string): BestPracticeCategory =>
  (CATEGORIES as readonly string[]).includes(c) ? (c as BestPracticeCategory) : "eo";

export const rowToItem = (r: Row): BestPractice => ({
  id: r.id,
  description: r.description,
  divisionTags: r.division_tags ?? [],
  category: asCategory(r.category),
  typicalCostNote: r.typical_cost_note ?? undefined,
  sourceNote: r.source_note ?? undefined,
  addedAt: (r.created_at ?? new Date().toISOString()).slice(0, 10),
});

const itemToRow = (b: BestPractice, orgId: string, userId: string) => ({
  org_id: orgId,
  user_id: userId,
  description: b.description,
  division_tags: b.divisionTags ?? [],
  category: asCategory(b.category),
  typical_cost_note: b.typicalCostNote ?? null,
  source_note: b.sourceNote ?? null,
});

/** Everything this org can see, oldest first so the list is stable. */
export async function loadOrgLessons(orgId: string): Promise<BestPractice[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from("best_practices")
    .select("id,org_id,user_id,description,division_tags,category,typical_cost_note,source_note,created_at")
    .eq("org_id", orgId)
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []).map(rowToItem);
}

/**
 * Give a brand-new org the built-in library, so a team's first look at
 * Lessons is useful rather than empty.
 *
 * ignoreDuplicates leans on the unique (org_id, description) index, which
 * makes this safe when two people open Settings on a new org at the same
 * moment -- the second insert is a no-op rather than a second copy.
 */
export async function seedOrgLessons(
  orgId: string, userId: string, defaults: BestPractice[],
): Promise<BestPractice[]> {
  if (!supabase || defaults.length === 0) return [];
  const { error } = await supabase
    .from("best_practices")
    .upsert(defaults.map(d => itemToRow(d, orgId, userId)),
            { onConflict: "org_id,description", ignoreDuplicates: true });
  if (error) throw new Error(error.message);
  return loadOrgLessons(orgId);
}

const same = (a: BestPractice, b: BestPractice) =>
  a.description === b.description &&
  a.category === b.category &&
  (a.typicalCostNote ?? "") === (b.typicalCostNote ?? "") &&
  (a.sourceNote ?? "") === (b.sourceNote ?? "") &&
  JSON.stringify(a.divisionTags ?? []) === JSON.stringify(b.divisionTags ?? []);

export interface SyncResult { items: BestPractice[]; errors: string[] }

/**
 * Apply the difference between two versions of the library.
 *
 * Returns the authoritative list read back from the server, because an
 * inserted row's id is assigned by Postgres -- keeping the client's temporary
 * id would make the next edit look like another insert.
 */
export async function syncOrgLessons(
  orgId: string, userId: string,
  prev: BestPractice[], next: BestPractice[],
): Promise<SyncResult> {
  if (!supabase) return { items: next, errors: [] };
  const prevById = new Map(prev.map(p => [p.id, p]));
  const nextById = new Map(next.map(n => [n.id, n]));
  const errors: string[] = [];

  const added   = next.filter(n => !prevById.has(n.id));
  const removed = prev.filter(p => !nextById.has(p.id));
  const changed = next.filter(n => {
    const before = prevById.get(n.id);
    return before && !same(before, n);
  });

  if (removed.length) {
    const { error } = await supabase.from("best_practices").delete()
      .in("id", removed.map(r => r.id));
    if (error) errors.push(`Could not delete: ${error.message}`);
  }
  for (const c of changed) {
    const { error } = await supabase.from("best_practices")
      .update(itemToRow(c, orgId, userId)).eq("id", c.id);
    if (error) errors.push(`Could not update "${c.description.slice(0, 40)}": ${error.message}`);
  }
  if (added.length) {
    const { error } = await supabase.from("best_practices")
      .upsert(added.map(a => itemToRow(a, orgId, userId)),
              { onConflict: "org_id,description", ignoreDuplicates: true });
    if (error) errors.push(`Could not add: ${error.message}`);
  }

  try {
    return { items: await loadOrgLessons(orgId), errors };
  } catch (e: any) {
    // The writes may well have landed; we just cannot confirm the order.
    return { items: next, errors: [...errors, e.message] };
  }
}
